import { Tabs } from "antd";
import React, { useEffect, useState } from "react";
import { ListItemLayoutDefault } from "../../list/ListItem.Layout";
import { ListV2 } from "../../list/ListV2";
import { Loader } from "../../loader/Loader";
import { SearchRequest } from "../../model/search/UserSearchRequestV1";
import { PageResponse } from "../../paging/Paging.Model";
import { RawContentAPI } from "../../raw-content/RawContent.API";
import { RawContentListItem } from "../../raw-content/RawContent.ListItem";
import {
  RawContent,
  RawContentRequest,
} from "../../raw-content/RawContent.Model";
import { EBButtonV2 } from "../common/EBButtonV2";
const { TabPane } = Tabs;

interface RawContentListTabProps {
  status: string;
  handleFetch: (
    searchRequest: SearchRequest | undefined,
    status: string
  ) => Promise<PageResponse<RawContent>>;
  handleEdit: (rawContent: RawContent) => void;
  handleDelete: (id: number) => void;
  handleRequeue: (id: number) => void;
  isLoading: boolean;
}

function RawContentListTab({
  status,
  handleFetch,
  handleEdit,
  handleDelete,
  handleRequeue,
  isLoading,
}: RawContentListTabProps) {
  return (
    <>
      {isLoading && <Loader />}
      <ListV2
        onFetch={(searchRequest?: SearchRequest) =>
          handleFetch(searchRequest, status)
        }
        ItemLayoutComponent={ListItemLayoutDefault}
        itemNode={(itemProps: any) => {
          let rawContent = itemProps.item;
          return (
            <RawContentListItem
              rawContent={rawContent}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onRequeue={handleRequeue}
            />
          );
        }}
      />
    </>
  );
}

export function AdminRawContentPage() {
  const [selectedRawContent, setSelectedRawContent] =
    useState<RawContent | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("PENDING");
  const [showForm, setShowForm] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchRawContents = (
    page: number,
    size: number,
    status: string
  ): Promise<PageResponse<RawContent>> => {
    setIsLoading(true);
    return RawContentAPI.fetchAllRawContent(page, size, status).then(
      (result) => {
        setIsLoading(false);
        return result;
      }
    );
  };

  const handleEdit = (rawContent: RawContent) => {
    setSelectedRawContent(rawContent);
    setIsEditing(true);
    setShowForm(true);
  };

  const handleDelete = (id: number) => {
    setIsLoading(true);
    RawContentAPI.deleteRawContent(id).then(() => {
      setIsLoading(false);
      setRefreshKey((prev) => prev + 1);
    });
  };

  const handleRequeue = (id: number) => {
    setIsLoading(true);
    RawContentAPI.requeueRawContent(id).then(() => {
      setIsLoading(false);
      setRefreshKey((prev) => prev + 1);
    });
  };

  const handleFormSubmit = (request: RawContentRequest) => {
    setIsLoading(true);
    if (isEditing && selectedRawContent) {
      RawContentAPI.updateRawContent(selectedRawContent.id, request).then(
        () => {
          setIsLoading(false);
          setIsEditing(false);
          setSelectedRawContent(null);
          setShowForm(false);
          setRefreshKey((prev) => prev + 1);
        }
      );
    } else {
      RawContentAPI.createRawContent(request).then(() => {
        setIsLoading(false);
        setSelectedRawContent(null);
        setShowForm(false);
        setRefreshKey((prev) => prev + 1);
      });
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setIsEditing(false);
    setSelectedRawContent(null);
  };

  const handleFetch = (
    searchRequest: SearchRequest | undefined,
    status: string
  ) => {
    const page = searchRequest?.pageParams?.page || 0;
    const size = searchRequest?.pageParams?.size || 10;
    return fetchRawContents(page, size, status);
  };

  const commonListProps = {
    handleFetch,
    handleEdit,
    handleDelete,
    handleRequeue,
    isLoading,
  };

  return (
    <div className="main-container">
      <h1>Raw Content Management</h1>

      {!showForm && (
        <EBButtonV2
          content={{ normal: "Create New Raw Content" }}
          onClick={() => {
            setSelectedRawContent(null);
            setIsEditing(false);
            setShowForm(true);
          }}
        />
      )}

      {showForm && (
        <RawContentForm
          onSubmit={handleFormSubmit}
          initialData={selectedRawContent}
          isEditing={isEditing}
          isLoading={isLoading}
          onCancel={handleCancelForm}
        />
      )}

      {!showForm && (
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="Pending" key="PENDING">
            <RawContentListTab
              status="PENDING"
              {...commonListProps}
              key={`pending-${refreshKey}`}
            />
          </TabPane>
          <TabPane tab="Completed" key="COMPLETED">
            <RawContentListTab
              status="COMPLETED"
              {...commonListProps}
              key={`completed-${refreshKey}`}
            />
          </TabPane>
          <TabPane tab="Failed" key="FAILED">
            <RawContentListTab
              status="FAILED"
              {...commonListProps}
              key={`failed-${refreshKey}`}
            />
          </TabPane>
        </Tabs>
      )}
    </div>
  );
}

interface RawContentFormProps {
  onSubmit: (request: RawContentRequest) => void;
  initialData: RawContent | null;
  isEditing: boolean;
  isLoading: boolean;
  onCancel: () => void;
}

function RawContentForm({
  onSubmit,
  initialData,
  isEditing,
  isLoading,
  onCancel,
}: RawContentFormProps) {
  const [content, setContent] = useState("");
  const [source, setSource] = useState("");
  const [useCase, setUseCase] = useState("INTERVIEW_EXPERIENCE");

  useEffect(() => {
    if (isEditing && initialData) {
      setContent(initialData.content);
      setSource(initialData.source);
      setUseCase(initialData.useCase);
    } else {
      setContent("");
      setSource("");
      setUseCase("INTERVIEW_EXPERIENCE");
    }
  }, [initialData, isEditing]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ content, source, useCase });
  };

  return (
    <form onSubmit={handleSubmit} className="p-3 border rounded mb-4">
      <div className="mb-3">
        <label htmlFor="content-textarea" className="form-label">
          Content
        </label>
        <textarea
          id="content-textarea"
          className="form-control"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Content"
          required
          rows={10}
        />
      </div>
      <div className="mb-3">
        <label htmlFor="source-input" className="form-label">
          Source
        </label>
        <input
          id="source-input"
          type="text"
          className="form-control"
          value={source}
          onChange={(e) => setSource(e.target.value)}
          placeholder="Source"
        />
      </div>
      <div className="mb-3">
        <label htmlFor="usecase-select" className="form-label">
          Use Case
        </label>
        <select
          id="usecase-select"
          className="form-select"
          value={useCase}
          onChange={(e) => setUseCase(e.target.value)}
        >
          <option value="INTERVIEW_EXPERIENCE">Interview Experience</option>
          <option value="POST">Post</option>
          <option value="QUESTION">Question</option>
        </select>
      </div>
      <div className="d-flex justify-content-end">
        <EBButtonV2
          content={{ normal: "Cancel" }}
          onClick={onCancel}
          className="me-2"
        />
        <EBButtonV2
          type="submit"
          loading={isLoading}
          content={{ normal: isEditing ? "Update" : "Create" }}
        />
      </div>
    </form>
  );
}